import Phaser from 'phaser';
import { getLevel, type Level } from '@/data/worlds';
import { audioManager } from '@/systems/AudioManager';
import { QuestionEngine } from '@/systems/QuestionEngine';
import { ScoreSystem } from '@/systems/ScoreSystem';
import type { PerTableStats, Question } from '@/types';
import { addBackdrop } from '@/ui/background';
import { Button } from '@/ui/components/Button';
import { ComboMeter } from '@/ui/components/ComboMeter';
import { Layout, Palette, Typography } from '@/ui/theme';
import { fadeInScene, fadeToScene } from '@/utils/transitions';
import type { ResultPayload } from '@/scenes/ResultScene';

export interface GameScenePayload {
  worldId: number;
  levelId: string;
}

const STARTING_LIVES = 3;
const FEEDBACK_DELAY_MS = 800;
const TIMER_BAR_WIDTH = 600;
const TIMER_BAR_HEIGHT = 14;

/** Fase de fallback quando alguém entra na GameScene sem payload. */
const DEFAULT_LEVEL: Level = {
  id: 'world-1-level-1',
  number: 1,
  name: 'Treino rápido',
  flavor: 'Aquecimento',
  tables: [1, 2, 5, 10],
  timeLimitMs: 8000,
  questionCount: 10,
  isBoss: false,
};

/**
 * GameScene — fase principal, agora com timer, combo e ScoreSystem.
 *
 * Loop por conta:
 *   1. Engine gera Question; HUD atualiza progresso.
 *   2. Timer começa (barra esvazia conforme tempo passa).
 *   3. Criança escolhe opção (ou tempo expira).
 *   4. ScoreSystem registra resultado, devolvendo pontos/combo/multiplicador.
 *   5. Feedback aparece sem tom punitivo (CLAUDE.md §7).
 *   6. Próxima conta (ou fim → ResultScene).
 */
export class GameScene extends Phaser.Scene {
  private engine!: QuestionEngine;
  private scoreSys!: ScoreSystem;
  private currentQuestion?: Question;
  private worldId = 1;
  private level: Level = DEFAULT_LEVEL;

  private lives = STARTING_LIVES;
  private livesLost = 0;
  private questionsAnswered = 0;
  private questionStartedAt = 0;
  private maxCombo = 0;
  private totalCorrectResponseTimeMs = 0;
  private perTable: Record<number, PerTableStats> = {};

  // HUD
  private hudLives?: Phaser.GameObjects.Text;
  private hudScore?: Phaser.GameObjects.Text;
  private hudProgress?: Phaser.GameObjects.Text;
  private comboMeter?: ComboMeter;

  // Question UI
  private questionLabel?: Phaser.GameObjects.Text;
  private feedbackLabel?: Phaser.GameObjects.Text;
  private timerFrame?: Phaser.GameObjects.Graphics;
  private timerFill?: Phaser.GameObjects.Graphics;
  private timerEvent?: Phaser.Time.TimerEvent;

  private answerButtons: Button[] = [];
  private acceptingInput = false;
  private startOverlay?: Phaser.GameObjects.Container;
  private hasStarted = false;

  constructor() {
    super({ key: 'GameScene' });
  }

  init(data?: Partial<GameScenePayload>): void {
    this.worldId = data?.worldId ?? 1;
    const levelFromData = data?.levelId ? getLevel(this.worldId, data.levelId) : undefined;
    this.level = levelFromData ?? DEFAULT_LEVEL;
  }

  create(): void {
    addBackdrop(this);
    fadeInScene(this);
    this.engine = new QuestionEngine({
      level: 1,
      tables: this.level.tables,
      timeLimitMs: this.level.timeLimitMs,
    });
    this.scoreSys = new ScoreSystem();
    this.lives = STARTING_LIVES;
    this.livesLost = 0;
    this.questionsAnswered = 0;
    this.maxCombo = 0;
    this.totalCorrectResponseTimeMs = 0;
    this.perTable = {};
    this.hasStarted = false;

    this.buildHud();
    this.buildQuestionArea();
    this.buildBackButton();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanupTimer());
    this.nextQuestion();
  }

  private buildHud(): void {
    const { width } = this.scale;

    this.hudLives = this.add.text(32, 24, this.formatLives(), {
      fontFamily: Typography.fontFamily,
      fontSize: '28px',
      color: Palette.dangerCss,
      fontStyle: 'bold',
    });

    this.hudScore = this.add
      .text(width / 2, 32, this.formatScore(), {
        fontFamily: Typography.fontFamily,
        fontSize: '28px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0);

    this.hudProgress = this.add
      .text(width - 32, 32, this.formatProgress(), {
        fontFamily: Typography.fontFamily,
        fontSize: '24px',
        color: Palette.textMutedCss,
      })
      .setOrigin(1, 0);

    this.comboMeter = new ComboMeter(this, width - 96, 110);
  }

  private buildQuestionArea(): void {
    const { width, height } = this.scale;

    this.questionLabel = this.add
      .text(width / 2, height * 0.3, '', {
        fontFamily: Typography.fontFamily,
        fontSize: '96px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    // Barra de tempo logo abaixo da conta.
    const barX = width / 2 - TIMER_BAR_WIDTH / 2;
    const barY = height * 0.4;
    this.timerFrame = this.add.graphics();
    this.timerFrame.lineStyle(2, Palette.textMuted, 1);
    this.timerFrame.strokeRoundedRect(barX - 2, barY - 2, TIMER_BAR_WIDTH + 4, TIMER_BAR_HEIGHT + 4, 6);

    this.timerFill = this.add.graphics();

    this.feedbackLabel = this.add
      .text(width / 2, height * 0.48, '', {
        fontFamily: Typography.fontFamily,
        fontSize: '28px',
        color: Palette.successCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
  }

  private buildBackButton(): void {
    const { width, height } = this.scale;
    new Button(this, width - 96, height - 48, 'Voltar', {
      width: 160,
      height: 48,
      fontSize: 18,
      fillColor: 0x2a2a4a,
      hoverColor: Palette.primary,
      onClick: () => {
        this.cleanupTimer();
        fadeToScene(this, 'LevelMapScene', { worldId: this.worldId });
      },
    });

    // Cabeçalho com nome da fase (ajuda a criança a se localizar).
    this.add
      .text(96, 70, `${this.level.isBoss ? 'BOSS · ' : ''}${this.level.name}`, {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.textMutedCss,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
  }

  private nextQuestion(): void {
    if (this.questionsAnswered >= this.level.questionCount || this.lives <= 0) {
      this.endRound();
      return;
    }

    // Antes do jogador clicar em JOGAR a primeira vez, a tela fica
    // limpa: nada de pergunta atrás do overlay nem botões clicáveis.
    if (!this.hasStarted) {
      this.showStartOverlay();
      return;
    }

    this.currentQuestion = this.engine.generate();
    this.feedbackLabel?.setText('');
    this.questionLabel?.setText(`${this.currentQuestion.factorA} × ${this.currentQuestion.factorB} = ?`);
    this.animateQuestionEntry();
    this.renderAnswerButtons(this.currentQuestion);
    this.armQuestion();
  }

  /** Liga timer + input após o jogador estar pronto. */
  private armQuestion(): void {
    this.startTimer();
    this.questionStartedAt = this.time.now;
    this.acceptingInput = true;
  }

  private showStartOverlay(): void {
    const { width, height } = this.scale;

    const backdrop = this.add
      .rectangle(0, 0, width, height, 0x000000, 0.55)
      .setOrigin(0)
      .setInteractive(); // bloqueia cliques nos botões da fase atrás

    const ready = this.add
      .text(width / 2, height * 0.4, 'Pronto?', {
        fontFamily: Typography.fontFamily,
        fontSize: '64px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const hint = this.add
      .text(width / 2, height * 0.4 + 60, 'O cronômetro só começa quando você clicar.', {
        fontFamily: Typography.fontFamily,
        fontSize: '18px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5);

    const playBtn = new Button(this, width / 2, height * 0.6, '▶ JOGAR', {
      width: 280,
      height: 80,
      fontSize: 32,
      fillColor: Palette.success,
      hoverColor: Palette.accent,
      onClick: () => this.dismissStartOverlay(),
    });

    this.startOverlay = this.add.container(0, 0, [backdrop, ready, hint, playBtn]).setDepth(1000);

    // Tecla Enter / Espaço também dispara o JOGAR — UX de teclado.
    const dismiss = () => this.dismissStartOverlay();
    this.input.keyboard?.once('keydown-ENTER', dismiss);
    this.input.keyboard?.once('keydown-SPACE', dismiss);
  }

  private dismissStartOverlay(): void {
    if (!this.startOverlay) return;
    this.startOverlay.destroy(true);
    this.startOverlay = undefined;
    this.hasStarted = true;
    // Agora sim gera e renderiza a primeira pergunta — antes do clique
    // a tela ficava limpa (sem conta vazada atrás do overlay).
    this.nextQuestion();
  }

  private renderAnswerButtons(question: Question): void {
    this.answerButtons.forEach((b) => b.destroy());
    this.answerButtons = [];

    const { width, height } = this.scale;
    const cols = 2;
    const buttonWidth = 220;
    const buttonHeight = 88;
    const gapX = 40;
    const gapY = 28;
    const totalWidth = cols * buttonWidth + (cols - 1) * gapX;
    const startX = width / 2 - totalWidth / 2 + buttonWidth / 2;
    const startY = height * 0.66;

    question.options.forEach((value, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = startX + col * (buttonWidth + gapX);
      const y = startY + row * (buttonHeight + gapY);

      const btn = new Button(this, x, y, String(value), {
        width: buttonWidth,
        height: buttonHeight,
        fontSize: 40,
        fillColor: Palette.primary,
        hoverColor: Palette.accent,
        cornerRadius: Layout.cornerRadius,
        onClick: () => this.onAnswer(value, index),
      });
      this.answerButtons.push(btn);
    });
  }

  private startTimer(): void {
    this.cleanupTimer();
    const limit = this.engine.getTimeLimitMs();
    this.drawTimer(1);
    this.timerEvent = this.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        const elapsed = this.time.now - this.questionStartedAt;
        const remaining = Math.max(0, 1 - elapsed / limit);
        this.drawTimer(remaining);
        if (remaining <= 0) {
          this.cleanupTimer();
          this.onTimeExpired();
        }
      },
    });
  }

  private drawTimer(ratio: number): void {
    if (!this.timerFill) return;
    const { width, height } = this.scale;
    const barX = width / 2 - TIMER_BAR_WIDTH / 2;
    const barY = height * 0.4;

    // Cor migra de accent → warning → danger conforme o tempo aperta.
    const color = ratio > 0.5 ? Palette.accent : ratio > 0.2 ? Palette.warning : Palette.danger;

    this.timerFill.clear();
    this.timerFill.fillStyle(color, 1);
    this.timerFill.fillRoundedRect(barX, barY, TIMER_BAR_WIDTH * ratio, TIMER_BAR_HEIGHT, 6);
  }

  private cleanupTimer(): void {
    this.timerEvent?.remove(false);
    this.timerEvent = undefined;
  }

  private onAnswer(value: number, buttonIndex: number): void {
    if (!this.acceptingInput || !this.currentQuestion) return;
    this.acceptingInput = false;
    this.cleanupTimer();
    this.drawTimer(0);

    const responseTime = this.time.now - this.questionStartedAt;
    const correct = value === this.currentQuestion.correctAnswer;
    this.engine.recordResult(this.currentQuestion, correct);
    this.questionsAnswered += 1;

    const clickedButton = this.answerButtons[buttonIndex];

    if (correct) {
      const result = this.scoreSys.recordCorrect(responseTime, this.engine.getTimeLimitMs());
      this.maxCombo = Math.max(this.maxCombo, result.combo);
      this.totalCorrectResponseTimeMs += responseTime;
      const turbo = result.speedBonus ? ' · Turbo!' : '';
      this.feedbackLabel?.setColor(Palette.successCss).setText(`+${result.points}${turbo}`);
      audioManager.playSfx(result.speedBonus ? 'correctTurbo' : 'correct', { combo: result.combo });
      clickedButton?.flashCorrect();
      this.spawnScorePopup(clickedButton, result.points, result.speedBonus);
      // Marco de combo a cada 5 acertos consecutivos — som extra empilha.
      if (result.combo > 0 && result.combo % 5 === 0) {
        audioManager.playSfx('combo', { combo: result.combo });
      }
    } else {
      this.scoreSys.recordWrong();
      this.lives -= 1;
      this.livesLost += 1;
      this.feedbackLabel
        ?.setColor(Palette.warningCss)
        .setText(`Quase! A resposta era ${this.currentQuestion.correctAnswer}.`);
      audioManager.playSfx('wrong');
      clickedButton?.flashWrong();
      this.highlightCorrectButton();
    }

    this.trackPerTable(this.currentQuestion, correct);
    this.refreshHud();
    this.time.delayedCall(FEEDBACK_DELAY_MS, () => this.nextQuestion());
  }

  /** Pequena animação de entrada da conta — sensação de "uma nova" surgindo. */
  private animateQuestionEntry(): void {
    if (!this.questionLabel) return;
    this.questionLabel.setScale(0.85);
    this.tweens.add({
      targets: this.questionLabel,
      scale: 1,
      duration: 220,
      ease: 'Back.easeOut',
    });
  }

  /** Localiza e destaca o botão da resposta certa quando a criança erra. */
  private highlightCorrectButton(): void {
    if (!this.currentQuestion) return;
    const correctIndex = this.currentQuestion.options.findIndex(
      (opt) => opt === this.currentQuestion!.correctAnswer,
    );
    if (correctIndex >= 0) this.answerButtons[correctIndex]?.highlightAsCorrect();
  }

  /** Pop-up flutuante "+15 Turbo!" subindo a partir do botão clicado. */
  private spawnScorePopup(origin: Phaser.GameObjects.Container | undefined, points: number, turbo: boolean): void {
    if (!origin) return;
    const text = turbo ? `+${points} Turbo!` : `+${points}`;
    const popup = this.add
      .text(origin.x, origin.y - origin.height / 2, text, {
        fontFamily: Typography.fontFamily,
        fontSize: '28px',
        color: turbo ? Palette.warningCss : Palette.successCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.tweens.add({
      targets: popup,
      y: popup.y - 60,
      alpha: { from: 1, to: 0 },
      duration: 700,
      ease: 'Cubic.easeOut',
      onComplete: () => popup.destroy(),
    });
  }

  private onTimeExpired(): void {
    if (!this.acceptingInput || !this.currentQuestion) return;
    this.acceptingInput = false;

    this.engine.recordResult(this.currentQuestion, false);
    this.scoreSys.recordWrong();
    this.lives -= 1;
    this.livesLost += 1;
    this.questionsAnswered += 1;
    this.trackPerTable(this.currentQuestion, false);

    this.feedbackLabel
      ?.setColor(Palette.textMutedCss)
      .setText(`O tempo acabou! Era ${this.currentQuestion.correctAnswer}.`);
    audioManager.playSfx('timeUp');
    this.highlightCorrectButton();

    this.refreshHud();
    this.time.delayedCall(FEEDBACK_DELAY_MS, () => this.nextQuestion());
  }

  private endRound(): void {
    this.cleanupTimer();
    this.answerButtons.forEach((b) => b.destroy());
    this.answerButtons = [];

    const snap = this.scoreSys.snapshot();
    const payload: ResultPayload = {
      completed: this.lives > 0,
      score: snap.score,
      correctCount: snap.correctCount,
      wrongCount: snap.wrongCount,
      livesLost: this.livesLost,
      speedBonusRate: snap.speedBonusRate,
      totalQuestions: this.level.questionCount,
      totalCorrectResponseTimeMs: this.totalCorrectResponseTimeMs,
      maxCombo: this.maxCombo,
      perTable: this.perTable,
      worldId: this.worldId,
      levelId: this.level.id,
      levelKey: this.level.id,
      levelName: this.level.name,
    };
    fadeToScene(this, 'ResultScene', payload);
  }

  /**
   * Registra a tentativa em ambos os fatores. Conhecer 7×8 reforça
   * tabuada do 7 e do 8 simultaneamente — então ambos contam.
   */
  private trackPerTable(question: Question, correct: boolean): void {
    const inc = (table: number) => {
      const stats = this.perTable[table] ?? { questions: 0, correct: 0 };
      this.perTable[table] = {
        questions: stats.questions + 1,
        correct: stats.correct + (correct ? 1 : 0),
      };
    };
    inc(question.factorA);
    if (question.factorB !== question.factorA) inc(question.factorB);
  }

  private refreshHud(): void {
    this.hudLives?.setText(this.formatLives());
    this.hudScore?.setText(this.formatScore());
    this.hudProgress?.setText(this.formatProgress());
    this.comboMeter?.update(this.scoreSys.getCombo(), this.scoreSys.getMultiplier());
  }

  private formatLives(): string {
    return '♥'.repeat(Math.max(0, this.lives)) + '♡'.repeat(Math.max(0, STARTING_LIVES - this.lives));
  }

  private formatScore(): string {
    return `${this.scoreSys?.getScore() ?? 0} pts`;
  }

  private formatProgress(): string {
    return `${this.questionsAnswered}/${this.level.questionCount}`;
  }
}
